import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '../../lib/utils';

const navLinks = [
  { name: 'HJEM', href: '/' },
  { name: 'SELVBETJENING', href: 'https://www.besoeglaegen.dk/Login.aspx?ClinicId=009555', external: true },
  { 
    name: 'PATIENT INFO', 
    href: '#',
    children: [
      { name: 'PRAKTISK INFO', href: '/praktisk-info' },
      { name: 'SCREENING', href: '/screening' },
      { name: 'REJSE VACCINATIONER', href: '/rejse-vaccinationer' },
      { name: 'KRONISKE LIDELSER', href: '/kroniske-lidelser' },
      { name: 'RIS OG ROS', href: '/ris-og-ros' },
      { name: 'NY PATIENT?', href: '/patient-info' },
      { name: 'KONSULTATIONER', href: '/konsultationer' },
      { name: 'GRAVIDE', href: '/gravide' },
      { name: 'BØRNEUNDERSØGELSER', href: '/boerneundersoegelser' },
      { name: 'PRISER', href: '/ydelser-og-priser' },
      { name: 'HENVISNINGER', href: '/henvisninger' },
    ]
  },
  { 
    name: 'OM OS', 
    href: '#',
    children: [
      { name: 'HVEM ER VI', href: '/hvem-er-vi' },
      { name: 'KONTAKT INFORMATIONER', href: '/kontakt' },
      { name: 'FIND VEJ', href: '/find-vej' },
      { name: 'HISTORIE', href: '/hvem-er-vi' },
    ]
  },
];

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [expandedLinks, setExpandedLinks] = useState({});
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close sidebar on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const toggleExpand = (name) => {
    setExpandedLinks(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };

  return (
    <>
      <header
        className={cn(
          'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
          isScrolled 
            ? 'bg-[#4b717a] py-3 shadow-md' 
            : 'bg-[#4b717a] py-6 shadow-md'
        )}
      >
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-4 group">
            <img 
              src="https://assets.auxodev.dk/assets/3d56beb5-25cd-44c2-a35b-95780bd285dd/e2a4c047-e995-4b00-9d03-e5cce2d849d8.png" 
              alt="Hellerup Lægehus Logo" 
              className={cn(
                "h-16 w-auto transition-all",
                isScrolled ? "h-12" : "h-16"
              )}
            />
          </Link>

          <button 
            className="p-2 transition-colors text-white flex items-center gap-3 group"
            onClick={() => setIsOpen(true)}
          >
            <span className="text-sm font-bold tracking-widest uppercase hidden sm:block group-hover:text-white/80">Menu</span>
            <Menu size={28} />
          </button>
        </div>
      </header>
      <div className="h-[112px] w-full shrink-0" />

      {/* Sidebar Overlay */}
      <div 
        className={cn(
          "fixed inset-0 bg-black/50 z-[60] transition-opacity duration-500",
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        )}
        onClick={() => setIsOpen(false)}
      />

      {/* Sidebar Menu */}
      <aside className={cn(
        "fixed top-0 right-0 bottom-0 w-full max-w-sm bg-[#4b717a] z-[70] shadow-2xl transition-transform duration-500 ease-in-out flex flex-col",
        isOpen ? "translate-x-0" : "translate-x-full"
      )}>
        {/* Sidebar Header */}
        <div className="p-6 flex justify-between items-center border-b border-white/10">
          <img 
            src="https://assets.auxodev.dk/assets/3d56beb5-25cd-44c2-a35b-95780bd285dd/e2a4c047-e995-4b00-9d03-e5cce2d849d8.png" 
            alt="Hellerup Lægehus Logo" 
            className="h-12 w-auto"
          />
          <button 
            className="p-2 text-white hover:text-white/80 transition-colors"
            onClick={() => setIsOpen(false)}
          >
            <X size={28} />
          </button>
        </div>

        {/* Sidebar Links */}
        <nav className="flex-grow overflow-y-auto py-8 px-6">
          <div className="flex flex-col gap-1">
            {navLinks.map((link) => (
              <div key={link.name} className="flex flex-col">
                <div className="flex items-center justify-between">
                  {link.external ? (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="py-3 text-lg font-bold tracking-widest text-white hover:text-white/80 transition-colors uppercase w-full"
                    >
                      {link.name}
                    </a>
                  ) : link.href === '#' ? (
                    <button
                      onClick={() => toggleExpand(link.name)}
                      className="py-3 text-lg font-bold tracking-widest text-white hover:text-white/80 transition-colors uppercase w-full text-left flex justify-between items-center"
                    >
                      {link.name}
                      {link.children && (
                        expandedLinks[link.name] ? <ChevronUp size={20} /> : <ChevronDown size={20} />
                      )}
                    </button>
                  ) : (
                    <Link
                      to={link.href}
                      className={cn(
                        "py-3 text-lg font-bold tracking-widest text-white hover:text-white/80 transition-colors uppercase w-full",
                        location.pathname === link.href && "text-white/90 underline underline-offset-4"
                      )}
                    >
                      {link.name}
                    </Link>
                  )}
                </div>

                {link.children && expandedLinks[link.name] && (
                  <div className="flex flex-col pl-4 border-l border-white/10 mt-1 mb-4 gap-1">
                    {link.children.map((child) => (
                      <Link
                        key={child.name}
                        to={child.href}
                        className={cn(
                          "py-2 text-sm font-bold tracking-widest text-white/70 hover:text-white transition-colors uppercase",
                          location.pathname === child.href && "text-white"
                        )}
                      >
                        {child.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </nav>

        {/* Sidebar Footer */}
        <div className="p-6 border-t border-white/10">
          <a 
            href="tel:70602202" 
            className="flex items-center justify-center gap-3 w-full py-4 border border-white text-white font-bold tracking-widest hover:bg-white hover:text-[#4b717a] transition-all uppercase"
          >
            <Phone size={20} />
            70 60 22 02
          </a>
        </div>
      </aside>
    </>
  );
}

